import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useGamification } from '../hooks/useGamification';
import {
  CreateBenefitDto,
  UpdateBenefitDto,
  BenefitRedemptionDto,
  CreateAchievementDto,
  UpdateAchievementDto,
  AchievementDto,
  CreateGamificationEventDto,
  UpdateGamificationEventDto,
  GamificationEventDto,
} from '@prometeus/contracts';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { Breadcrumb } from '../components/Breadcrumb';

export const Route = createFileRoute('/w/$workspaceId/settings/gamification')({
  component: GamificationSettingsPage,
});

function GamificationSettingsPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);

  const canManagePoints = hasPermission('gamification.config.manage');
  const canManageBenefits = hasPermission('gamification.benefit.manage');
  const canCreateBenefits = hasPermission('gamification.benefit.create');
  const canManageRedemptions = hasPermission('gamification.redemption.manage');
  const canAccessSettings = canManagePoints || canManageBenefits || canCreateBenefits || canManageRedemptions;

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canAccessSettings) {
      showPermissionDenied('Gerenciar configurações de gamificação');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canAccessSettings, showPermissionDenied, navigate, workspaceId]);

  const {
    pointsConfig,
    //isLoadingPointsConfig,
    benefitsManagement,
    //isLoadingBenefitsManagement,
    redemptions,
    //isLoadingRedemptions,
    createBenefit,
    updateBenefit,
    deleteBenefit,
    updatePointsConfig,
    approveRedemption,
    rejectRedemption,
    fulfillRedemption,
    isCreatingBenefit,
    //isUpdatingBenefit,
    //isDeletingBenefit,
    isUpdatingPointsConfig,
    isApprovingRedemption,
    isRejectingRedemption,
    isFulfillingRedemption,
    achievements,
    isLoadingAchievements,
    events,
    isLoadingEvents,
    createAchievement,
    updateAchievement,
    deleteAchievement,
    isCreatingAchievement,
    isUpdatingAchievement,
    isDeletingAchievement,
    createEvent,
    updateEvent,
    deleteEvent,
    isCreatingEvent,
    isUpdatingEvent,
    isDeletingEvent,
  } = useGamification(workspaceId, { enabled: canAccessSettings, variant: 'settings' });

  const [activeTab, setActiveTab] = useState<'points' | 'benefits' | 'redemptions' | 'achievements' | 'events'>('points');
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState<string | null>(null);
  const [approvingRedemption, setApprovingRedemption] = useState<string | null>(null);
  const [rejectingRedemption, setRejectingRedemption] = useState<string | null>(null);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<string | null>(null);
  const [achievementForm, setAchievementForm] = useState<CreateAchievementDto>({
    name: '',
    description: '',
    icon: '',
    conditionType: 'TASK_COUNT',
    conditionValue: 1,
    xpReward: 0,
    pointsReward: 0,
    isActive: true,
  });
  const [eventForm, setEventForm] = useState<CreateGamificationEventDto>({
    name: '',
    description: '',
    eventType: 'POINTS_MULTIPLIER',
    multiplier: 2.0,
    validFrom: null,
    validUntil: null,
    durationHours: null,
    isActive: true,
  });
  const [eventDurationMethod, setEventDurationMethod] = useState<'dateRange' | 'hours'>('dateRange');

  // Form states
  const [pointsForm, setPointsForm] = useState({
    taskPoints: pointsConfig?.taskPoints || 10,
    deadlinePoints: pointsConfig?.deadlinePoints || 15,
    eventPoints: pointsConfig?.eventPoints || 20,
    hearingPoints: pointsConfig?.hearingPoints || 25,
  });

  const [benefitForm, setBenefitForm] = useState<CreateBenefitDto>({
    name: '',
    description: '',
    pointsCost: 0,
    benefitType: 'OTHER',
    isActive: true,
    stock: null,
    maxPerMember: null,
    validFrom: null,
    validUntil: null,
    category: null,
    featured: false,
  });

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
  });

  if (!hasPermission('gamification.config.manage')) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div 
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <p 
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
                textAlign: 'center',
              }}
            >
              Você não tem permissão para acessar esta página.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSavePointsConfig = () => {
    updatePointsConfig(pointsForm, {
      onSuccess: () => {
        alert('Configuração de pontos salva com sucesso!');
      },
      onError: (error: any) => {
        alert(error.response?.data?.message || 'Erro ao salvar configuração');
      },
    });
  };

  const handleCreateBenefit = () => {
    if (!(hasPermission('gamification.benefit.create') || hasPermission('gamification.benefit.manage'))) {
      requirePermission('gamification.benefit.create', 'Criar benefícios');
      return;
    }
    createBenefit(benefitForm, {
      onSuccess: () => {
        setShowBenefitModal(false);
        setBenefitForm({
          name: '',
          description: '',
          pointsCost: 0,
          benefitType: 'OTHER',
          isActive: true,
          stock: null,
          maxPerMember: null,
          validFrom: null,
          validUntil: null,
          category: null,
          featured: false,
        });
      },
      onError: (error: any) => {
        alert(error.response?.data?.message || 'Erro ao criar benefício');
      },
    });
  };

  const handleUpdateBenefit = (benefitId: string, dto: UpdateBenefitDto) => {
    if (!requirePermission('gamification.benefit.manage', 'Gerenciar benefícios')) return;
    updateBenefit({ benefitId, dto }, {
      onSuccess: () => {
        setEditingBenefit(null);
      },
      onError: (error: any) => {
        alert(error.response?.data?.message || 'Erro ao atualizar benefício');
      },
    });
  };

  const handleDeleteBenefit = (benefitId: string) => {
    if (!requirePermission('gamification.benefit.manage', 'Gerenciar benefícios')) return;
    if (confirm('Tem certeza que deseja desativar este benefício?')) {
      deleteBenefit(benefitId, {
        onError: (error: any) => {
          alert(error.response?.data?.message || 'Erro ao desativar benefício');
        },
      });
    }
  };

  const handleApproveRedemption = (redemptionId: string) => {
    if (!requirePermission('gamification.redemption.manage', 'Gerenciar resgates')) return;
    const notes = prompt('Notas (opcional):');
    approveRedemption({ redemptionId, dto: { notes: notes || undefined } }, {
      onSuccess: () => {
        setApprovingRedemption(null);
      },
      onError: (error: any) => {
        setApprovingRedemption(null);
        alert(error.response?.data?.message || 'Erro ao aprovar resgate');
      },
    });
  };

  const handleRejectRedemption = (redemptionId: string) => {
    if (!requirePermission('gamification.redemption.manage', 'Gerenciar resgates')) return;
    const notes = prompt('Motivo da rejeição:');
    if (notes) {
      rejectRedemption({ redemptionId, dto: { notes } }, {
        onSuccess: () => {
          setRejectingRedemption(null);
        },
        onError: (error: any) => {
          setRejectingRedemption(null);
          alert(error.response?.data?.message || 'Erro ao rejeitar resgate');
        },
      });
    }
  };

  const handleFulfillRedemption = (redemptionId: string) => {
    if (!requirePermission('gamification.redemption.manage', 'Gerenciar resgates')) return;
    if (confirm('Marcar este resgate como cumprido?')) {
      fulfillRedemption(redemptionId, {
        onError: (error: any) => {
          alert(error.response?.data?.message || 'Erro ao marcar como cumprido');
        },
      });
    }
  };

  const getBenefitTypeLabel = (type: string) => {
    switch (type) {
      case 'DAY_OFF': return 'Dia de Folga';
      case 'PRIZE': return 'Prêmio';
      case 'DISCOUNT': return 'Desconto';
      case 'EXPERIENCE_BOOST': return 'Boost de XP';
      case 'STREAK_SHIELD': return 'Escudo de Streak';
      case 'CUSTOM_BADGE': return 'Badge';
      case 'PRIORITY_SUPPORT': return 'Suporte VIP';
      case 'EXTRA_STORAGE': return 'Armazenamento';
      default: return 'Outro';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendente';
      case 'APPROVED': return 'Aprovado';
      case 'REJECTED': return 'Rejeitado';
      case 'FULFILLED': return 'Cumprido';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-blue-100 text-blue-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'FULFILLED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const pendingRedemptions = redemptions?.filter((r) => r.status === 'PENDING') || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
            { label: 'Gamificação' },
          ]}
        />
        
        <div 
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '0.25rem',
                }}
              >
                Configuração de Gamificação
              </h1>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  fontWeight: 400,
                }}
              >
                Gerencie pontos, benefícios e resgates do sistema de gamificação
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('points')}
                className={`py-4 px-1 border-b-2 transition-colors ${
                  activeTab === 'points'
                    ? 'border-[#485A6A]'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 500,
                  color: activeTab === 'points' ? '#485A6A' : '#718096',
                }}
              >
                Configuração de Pontos
              </button>
              <button
                onClick={() => setActiveTab('benefits')}
                className={`py-4 px-1 border-b-2 transition-colors ${
                  activeTab === 'benefits'
                    ? 'border-[#485A6A]'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 500,
                  color: activeTab === 'benefits' ? '#485A6A' : '#718096',
                }}
              >
                Gerenciar Benefícios
              </button>
              <button
                onClick={() => setActiveTab('redemptions')}
                className={`py-4 px-1 border-b-2 transition-colors relative ${
                  activeTab === 'redemptions'
                    ? 'border-[#485A6A]'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 500,
                  color: activeTab === 'redemptions' ? '#485A6A' : '#718096',
                }}
              >
                Resgates Pendentes
                {pendingRedemptions.length > 0 && (
                  <span 
                    className="ml-2 px-2 py-0.5 text-xs rounded-full"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      backgroundColor: '#EF4444',
                      color: '#FFFFFF',
                    }}
                  >
                    {pendingRedemptions.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('achievements')}
                className={`py-4 px-1 border-b-2 transition-colors ${
                  activeTab === 'achievements'
                    ? 'border-[#485A6A]'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 500,
                  color: activeTab === 'achievements' ? '#485A6A' : '#718096',
                }}
              >
                Conquistas
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`py-4 px-1 border-b-2 transition-colors ${
                  activeTab === 'events'
                    ? 'border-[#485A6A]'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 500,
                  color: activeTab === 'events' ? '#485A6A' : '#718096',
                }}
              >
                Eventos & Bônus
              </button>
            </nav>
          </div>

          {/* Points Config Tab */}
          {activeTab === 'points' && (
            <div 
              className="p-6"
              style={{
                backgroundColor: '#F9FAFB',
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <h2 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '1.5rem',
                }}
              >
                Pontos por Tipo de Task
              </h2>
              <div className="space-y-4">
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Pontos para TASK
                  </label>
                  <input
                    type="number"
                    value={pointsForm.taskPoints}
                    onChange={(e) => setPointsForm({ ...pointsForm, taskPoints: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px]"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Pontos para DEADLINE
                  </label>
                  <input
                    type="number"
                    value={pointsForm.deadlinePoints}
                    onChange={(e) => setPointsForm({ ...pointsForm, deadlinePoints: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px]"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Pontos para EVENT
                  </label>
                  <input
                    type="number"
                    value={pointsForm.eventPoints}
                    onChange={(e) => setPointsForm({ ...pointsForm, eventPoints: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px]"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Pontos para HEARING
                  </label>
                  <input
                    type="number"
                    value={pointsForm.hearingPoints}
                    onChange={(e) => setPointsForm({ ...pointsForm, hearingPoints: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px]"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                  />
                </div>
                <button
                  onClick={handleSavePointsConfig}
                  disabled={isUpdatingPointsConfig}
                  className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#03131B',
                    color: '#FFFFFF',
                  }}
                >
                  {isUpdatingPointsConfig ? 'Salvando...' : 'Salvar Configuração'}
                </button>
              </div>
            </div>
          )}

          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <div className="space-y-4">
              <div 
                className="flex justify-between items-center p-6 rounded-lg"
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '15px',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <img src="/Gestao_de_beneficios.svg" alt="Gestão de Benefícios" className="w-12 h-12" />
                  </div>
                  <div>
                    <h2 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2D3748',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Gestão de Benefícios
                    </h2>
                    <p 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      Crie e gerencie os benefícios disponíveis na loja
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!(hasPermission('gamification.benefit.create') || hasPermission('gamification.benefit.manage'))) {
                      requirePermission('gamification.benefit.create', 'Criar benefícios');
                      return;
                    }
                    setShowBenefitModal(true);
                  }}
                    className="rounded-lg transition-colors hover:opacity-80"
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#F9FAFB',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Criar Benefício"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="#2D3748"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
              </div>

              {benefitsManagement && benefitsManagement.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead 
                      style={{
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <tr>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Nome
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Tipo
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Pontos
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Estoque
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Categoria
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Status
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {benefitsManagement.map((benefit) => (
                        <tr key={benefit.id}>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {benefit.name}
                              {benefit.featured && (
                                <span className="text-yellow-500" title="Destaque">★</span>
                              )}
                            </div>
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {getBenefitTypeLabel(benefit.benefitType)}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {benefit.pointsCost}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: benefit.stock !== null && benefit.stock <= 5 ? '#EF4444' : '#718096',
                            }}
                          >
                            {benefit.stock !== null ? benefit.stock : '∞'}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {benefit.category || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span 
                              className={`px-2 py-1 text-xs rounded-full ${benefit.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              {benefit.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const newActive = !benefit.isActive;
                                  handleUpdateBenefit(benefit.id, { isActive: newActive });
                                }}
                                className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#F9FAFB',
                                  color: '#2D3748',
                                }}
                              >
                                {benefit.isActive ? 'Desativar' : 'Ativar'}
                              </button>
                              <button
                                onClick={() => handleDeleteBenefit(benefit.id)}
                                className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#F9FAFB',
                                  color: '#EF4444',
                                }}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div 
                  className="p-8 text-center"
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Nenhum benefício criado ainda.
                  </p>
                </div>
              )}

              {/* Create Benefit Modal */}
              {showBenefitModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
                  <div 
                    className="bg-white p-8 max-w-md w-full"
                    style={{
                      borderRadius: '15px',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h3 
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#2D3748',
                        }}
                      >
                        Criar Benefício
                      </h3>
                      <button 
                        onClick={() => setShowBenefitModal(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label 
                          className="block mb-2"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Nome <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={benefitForm.name}
                          onChange={(e) => setBenefitForm({ ...benefitForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                        />
                      </div>
                      <div>
                        <label 
                          className="block mb-2"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Descrição
                        </label>
                        <textarea
                          value={benefitForm.description}
                          onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                          rows={3}
                        />
                      </div>
                      <div>
                        <label 
                          className="block mb-2"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Custo em Pontos <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="number"
                          value={benefitForm.pointsCost}
                          onChange={(e) => setBenefitForm({ ...benefitForm, pointsCost: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                        />
                      </div>
                      <div>
                        <label 
                          className="block mb-2"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Tipo
                        </label>
                        <select
                          value={benefitForm.benefitType}
                          onChange={(e) => setBenefitForm({ ...benefitForm, benefitType: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                        >
                          <option value="DAY_OFF">Dia de Folga</option>
                          <option value="PRIZE">Prêmio</option>
                          <option value="DISCOUNT">Desconto</option>
                          <option value="EXPERIENCE_BOOST">Boost de Experiência</option>
                          <option value="STREAK_SHIELD">Escudo de Streak</option>
                          <option value="CUSTOM_BADGE">Badge Personalizado</option>
                          <option value="PRIORITY_SUPPORT">Suporte Prioritário</option>
                          <option value="EXTRA_STORAGE">Armazenamento Extra</option>
                          <option value="OTHER">Outro</option>
                        </select>
                      </div>
                      <div>
                        <label 
                          className="block mb-2"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Categoria
                        </label>
                        <input
                          type="text"
                          value={benefitForm.category || ''}
                          onChange={(e) => setBenefitForm({ ...benefitForm, category: e.target.value || null })}
                          placeholder="Ex: Lazer, Trabalho, Premium"
                          className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label 
                            className="block mb-2"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#718096',
                            }}
                          >
                            Estoque (vazio = ilimitado)
                          </label>
                          <input
                            type="number"
                            value={benefitForm.stock ?? ''}
                            onChange={(e) => setBenefitForm({ ...benefitForm, stock: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Ilimitado"
                            className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          />
                        </div>
                        <div>
                          <label 
                            className="block mb-2"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#718096',
                            }}
                          >
                            Máx. por membro
                          </label>
                          <input
                            type="number"
                            value={benefitForm.maxPerMember ?? ''}
                            onChange={(e) => setBenefitForm({ ...benefitForm, maxPerMember: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Ilimitado"
                            className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label 
                            className="block mb-2"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#718096',
                            }}
                          >
                            Válido a partir de
                          </label>
                          <input
                            type="date"
                            value={benefitForm.validFrom ? new Date(benefitForm.validFrom).toISOString().split('T')[0] : ''}
                            onChange={(e) => setBenefitForm({ ...benefitForm, validFrom: e.target.value ? new Date(e.target.value) : null })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          />
                        </div>
                        <div>
                          <label 
                            className="block mb-2"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#718096',
                            }}
                          >
                            Válido até
                          </label>
                          <input
                            type="date"
                            value={benefitForm.validUntil ? new Date(benefitForm.validUntil).toISOString().split('T')[0] : ''}
                            onChange={(e) => setBenefitForm({ ...benefitForm, validUntil: e.target.value ? new Date(e.target.value) : null })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="featured"
                          checked={benefitForm.featured || false}
                          onChange={(e) => setBenefitForm({ ...benefitForm, featured: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label 
                          htmlFor="featured"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#718096',
                          }}
                        >
                          Destacar na loja
                        </label>
                      </div>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setShowBenefitModal(false)}
                          className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            backgroundColor: '#F9FAFB',
                            color: '#2D3748',
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleCreateBenefit}
                          disabled={!benefitForm.name || !benefitForm.pointsCost || isCreatingBenefit}
                          className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            backgroundColor: '#03131B',
                            color: '#FFFFFF',
                          }}
                        >
                          {isCreatingBenefit ? 'Criando...' : 'Criar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
        </div>
      )}

          {/* Redemptions Tab */}
          {activeTab === 'redemptions' && (
            <div>
              <h2 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '1.5rem',
                }}
              >
                Resgates Pendentes
              </h2>
              {pendingRedemptions.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead 
                      style={{
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <tr>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Membro
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Benefício
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Pontos
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Data
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingRedemptions.map((redemption: BenefitRedemptionDto) => (
                        <tr key={redemption.id}>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            {redemption.membership?.user?.name || 'Desconhecido'}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            {redemption.benefit?.name || 'Benefício removido'}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {redemption.pointsSpent}
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {new Date(redemption.redeemedAt).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveRedemption(redemption.id)}
                                disabled={isApprovingRedemption || approvingRedemption === redemption.id}
                                className="px-3 py-1.5 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#10B981',
                                  color: '#FFFFFF',
                                }}
                              >
                                Aprovar
                              </button>
                              <button
                                onClick={() => handleRejectRedemption(redemption.id)}
                                disabled={isRejectingRedemption || rejectingRedemption === redemption.id}
                                className="px-3 py-1.5 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#EF4444',
                                  color: '#FFFFFF',
                                }}
                              >
                                Rejeitar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div 
                  className="p-8 text-center"
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Nenhum resgate pendente no momento.
                  </p>
                </div>
              )}

              {/* All Redemptions */}
              {redemptions && redemptions.length > pendingRedemptions.length && (
                <div className="mt-8">
                  <h3 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#2D3748',
                      marginBottom: '1.5rem',
                    }}
                  >
                    Todos os Resgates
                  </h3>
                  <div className="overflow-hidden">
                    <table className="w-full">
                      <thead 
                        style={{
                          backgroundColor: '#F9FAFB',
                        }}
                      >
                        <tr>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Membro
                          </th>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Benefício
                          </th>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Pontos
                          </th>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Status
                          </th>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Data
                          </th>
                          <th 
                            className="px-4 py-3 text-left uppercase tracking-wider"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#718096',
                            }}
                          >
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {redemptions.filter((r) => r.status !== 'PENDING').map((redemption: BenefitRedemptionDto) => (
                          <tr key={redemption.id}>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              {redemption.membership?.user?.name || 'Desconhecido'}
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              {redemption.benefit?.name || 'Benefício removido'}
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#718096',
                              }}
                            >
                              {redemption.pointsSpent}
                            </td>
                            <td className="px-4 py-3">
                              <span 
                                className={`px-2 py-1 text-xs rounded-full ${getStatusColor(redemption.status)}`}
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                }}
                              >
                                {getStatusLabel(redemption.status)}
                              </span>
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#718096',
                              }}
                            >
                              {new Date(redemption.redeemedAt).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="px-4 py-3">
                              {redemption.status === 'APPROVED' && (
                                <button
                                  onClick={() => handleFulfillRedemption(redemption.id)}
                                  disabled={isFulfillingRedemption}
                                  className="px-3 py-1.5 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#10B981',
                                    color: '#FFFFFF',
                                  }}
                                >
                                  Marcar como Cumprido
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <div className="space-y-4">
              <div 
                className="flex justify-between items-center p-6 rounded-lg"
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '15px',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#FEF3C7' }}
                  >
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div>
                    <h2 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2D3748',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Sistema de Conquistas
                    </h2>
                    <p 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      Crie conquistas para motivar sua equipe
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAchievementModal(true)}
                  className="rounded-lg transition-colors hover:opacity-80"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Criar Conquista"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="#2D3748"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Achievement Types Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#F0FDF4',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">✅</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#166534' }}>
                      Tarefas Completadas
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#15803D' }}>
                    Ex: "Primeira Task" (1 task), "Produtivo" (100 tasks)
                  </p>
                </div>
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#FEF3C7',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">🔥</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#92400E' }}>
                      Streak
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#B45309' }}>
                    Ex: "Consistente" (7 dias), "Imbatível" (30 dias)
                  </p>
                </div>
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#EEF2FF',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">⭐</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#3730A3' }}>
                      Pontos Acumulados
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#4338CA' }}>
                    Ex: "Iniciante" (100 pts), "Mestre" (10.000 pts)
                  </p>
                </div>
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#FCE7F3',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">📈</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#9D174D' }}>
                      Level
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#BE185D' }}>
                    Ex: "Nível 5", "Nível 10", "Lendário"
                  </p>
                </div>
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#ECFDF5',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">🏅</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#065F46' }}>
                      Ranking
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#047857' }}>
                    Ex: "Top 3", "Líder", "Campeão Semanal"
                  </p>
                </div>
                <div 
                  className="p-4"
                  style={{
                    backgroundColor: '#F5F3FF',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">🎁</span>
                    <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: 600, color: '#5B21B6' }}>
                      Resgates
                    </h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#6D28D9' }}>
                    Ex: "Primeiro Resgate", "Colecionador"
                  </p>
                </div>
              </div>

              {achievements && achievements.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead 
                      style={{
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <tr>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ícone
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Nome
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Condição
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Recompensas
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Status
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {achievements.map((achievement) => (
                        <tr key={achievement.id}>
                          <td className="px-4 py-3">
                            <span className="text-2xl">{achievement.icon || '🏆'}</span>
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#2D3748',
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>{achievement.name}</div>
                              {achievement.description && (
                                <div style={{ fontSize: '11px', color: '#718096', marginTop: '0.25rem' }}>
                                  {achievement.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            <div>
                              {achievement.conditionType === 'TASK_COUNT' && `${achievement.conditionValue} tarefas`}
                              {achievement.conditionType === 'TASK_STREAK' && `${achievement.conditionValue} dias de streak`}
                              {achievement.conditionType === 'LEVEL_REACHED' && `Level ${achievement.conditionValue}`}
                              {achievement.conditionType === 'POINTS_EARNED' && `${achievement.conditionValue} pontos`}
                              {achievement.conditionType === 'DAYS_ACTIVE' && `${achievement.conditionValue} dias ativos`}
                            </div>
                          </td>
                          <td 
                            className="px-4 py-3"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              color: '#718096',
                            }}
                          >
                            {achievement.xpReward > 0 && <div>XP: {achievement.xpReward}</div>}
                            {achievement.pointsReward > 0 && <div>Pontos: {achievement.pointsReward}</div>}
                            {achievement.xpReward === 0 && achievement.pointsReward === 0 && '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span 
                              className={`px-2 py-1 text-xs rounded-full ${achievement.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            >
                              {achievement.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const newActive = !achievement.isActive;
                                  updateAchievement({ achievementId: achievement.id, dto: { isActive: newActive } }, {
                                    onError: (error: any) => {
                                      alert(error.response?.data?.message || 'Erro ao atualizar conquista');
                                    },
                                  });
                                }}
                                className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#F9FAFB',
                                  color: '#2D3748',
                                }}
                              >
                                {achievement.isActive ? 'Desativar' : 'Ativar'}
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Tem certeza que deseja desativar esta conquista?')) {
                                    deleteAchievement(achievement.id, {
                                      onError: (error: any) => {
                                        alert(error.response?.data?.message || 'Erro ao desativar conquista');
                                      },
                                    });
                                  }
                                }}
                                className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                style={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontSize: '12px',
                                  backgroundColor: '#F9FAFB',
                                  color: '#EF4444',
                                }}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div 
                  className="p-8 text-center"
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <span className="text-4xl mb-4 block">🏆</span>
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '14px',
                      color: '#2D3748',
                      fontWeight: 500,
                      marginBottom: '0.5rem',
                    }}
                  >
                    Nenhuma conquista criada ainda
                  </p>
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Clique no botão + acima para criar sua primeira conquista
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
              <div 
                className="flex justify-between items-center p-6 rounded-lg"
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '15px',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#DBEAFE' }}
                  >
                    <span className="text-2xl">⚡</span>
                  </div>
                  <div>
                    <h2 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2D3748',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Eventos Especiais & Bônus
                    </h2>
                    <p 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      Configure multiplicadores de pontos para datas especiais
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEventModal(true)}
                  className="rounded-lg transition-colors hover:opacity-80"
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Criar Evento"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="#2D3748"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Event Types */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className="p-6"
                  style={{
                    backgroundColor: '#FEF3C7',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🎉</span>
                    <div>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#92400E' }}>
                        Evento de Pontos 2x
                      </h3>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#B45309' }}>
                        Dobre os pontos por tempo limitado
                      </p>
                    </div>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#78350F' }}>
                    Ideal para datas comemorativas, final de mês ou sprints especiais.
                  </p>
                </div>
                <div 
                  className="p-6"
                  style={{
                    backgroundColor: '#DBEAFE',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🚀</span>
                    <div>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1E40AF' }}>
                        Boost de XP
                      </h3>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#1D4ED8' }}>
                        Acelere o progresso de nível
                      </p>
                    </div>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#1E3A8A' }}>
                    Perfeito para incentivar a evolução da equipe em períodos específicos.
                  </p>
                </div>
                <div 
                  className="p-6"
                  style={{
                    backgroundColor: '#F0FDF4',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🎯</span>
                    <div>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#166534' }}>
                        Desafio em Equipe
                      </h3>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#15803D' }}>
                        Meta coletiva com recompensa
                      </p>
                    </div>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#14532D' }}>
                    Define uma meta de tarefas para a equipe com bônus para todos ao atingir.
                  </p>
                </div>
                <div 
                  className="p-6"
                  style={{
                    backgroundColor: '#FCE7F3',
                    borderRadius: '15px',
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🎁</span>
                    <div>
                      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', fontWeight: 600, color: '#9D174D' }}>
                        Loja em Promoção
                      </h3>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '11px', color: '#BE185D' }}>
                        Desconto em benefícios
                      </p>
                    </div>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#831843' }}>
                    Reduza o custo de benefícios por tempo limitado.
                  </p>
                </div>
              </div>

              {events && events.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="w-full">
                    <thead 
                      style={{
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <tr>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Nome
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Tipo
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Multiplicador
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Período
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Status
                        </th>
                        <th 
                          className="px-4 py-3 text-left uppercase tracking-wider"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#718096',
                          }}
                        >
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {events.map((event) => {
                        const now = new Date();
                        const validFrom = event.validFrom ? new Date(event.validFrom) : null;
                        const validUntil = event.validUntil ? new Date(event.validUntil) : null;
                        const endTime = validUntil || (event.durationHours ? new Date(new Date(event.createdAt).getTime() + event.durationHours * 60 * 60 * 1000) : null);
                        const isActiveNow = event.isActive && (
                          (validFrom && validUntil && now >= validFrom && now <= validUntil) ||
                          (event.durationHours && endTime && now <= endTime)
                        );
                        const isFuture = validFrom && now < validFrom;
                        const isExpired = endTime && now > endTime;

                        return (
                          <tr key={event.id}>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#2D3748',
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 500 }}>{event.name}</div>
                                {event.description && (
                                  <div style={{ fontSize: '11px', color: '#718096', marginTop: '0.25rem' }}>
                                    {event.description}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#718096',
                              }}
                            >
                              {event.eventType === 'POINTS_MULTIPLIER' && 'Pontos 2x'}
                              {event.eventType === 'XP_MULTIPLIER' && 'Boost XP'}
                              {event.eventType === 'CUSTOM' && 'Personalizado'}
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#718096',
                              }}
                            >
                              {event.multiplier}x
                            </td>
                            <td 
                              className="px-4 py-3"
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                color: '#718096',
                              }}
                            >
                              {validFrom && validUntil ? (
                                <div>
                                  <div>{new Date(validFrom).toLocaleDateString('pt-BR')}</div>
                                  <div style={{ fontSize: '11px' }}>até {new Date(validUntil).toLocaleDateString('pt-BR')}</div>
                                </div>
                              ) : event.durationHours ? (
                                <div>
                                  {event.durationHours}h após criação
                                  {endTime && (
                                    <div style={{ fontSize: '11px' }}>Termina: {endTime.toLocaleDateString('pt-BR')}</div>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {isActiveNow ? (
                                <span 
                                  className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                  }}
                                >
                                  Ativo
                                </span>
                              ) : isFuture ? (
                                <span 
                                  className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                  }}
                                >
                                  Futuro
                                </span>
                              ) : isExpired ? (
                                <span 
                                  className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                  }}
                                >
                                  Expirado
                                </span>
                              ) : (
                                <span 
                                  className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                  }}
                                >
                                  {event.isActive ? 'Ativo' : 'Inativo'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const newActive = !event.isActive;
                                    updateEvent({ eventId: event.id, dto: { isActive: newActive } }, {
                                      onError: (error: any) => {
                                        alert(error.response?.data?.message || 'Erro ao atualizar evento');
                                      },
                                    });
                                  }}
                                  className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    color: '#2D3748',
                                  }}
                                >
                                  {event.isActive ? 'Desativar' : 'Ativar'}
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm('Tem certeza que deseja desativar este evento?')) {
                                      deleteEvent(event.id, {
                                        onError: (error: any) => {
                                          alert(error.response?.data?.message || 'Erro ao desativar evento');
                                        },
                                      });
                                    }
                                  }}
                                  className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    backgroundColor: '#F9FAFB',
                                    color: '#EF4444',
                                  }}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div 
                  className="p-8 text-center"
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <span className="text-4xl mb-4 block">⚡</span>
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '14px',
                      color: '#2D3748',
                      fontWeight: 500,
                      marginBottom: '0.5rem',
                    }}
                  >
                    Nenhum evento ativo no momento
                  </p>
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#718096',
                    }}
                  >
                    Clique no botão + acima para criar um evento especial
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Achievement Modal */}
      {showAchievementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
          <div 
            className="bg-white p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Criar Conquista
              </h3>
              <button 
                onClick={() => setShowAchievementModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Nome <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={achievementForm.name}
                  onChange={(e) => setAchievementForm({ ...achievementForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  placeholder="Ex: Primeira Task, Consistente, Mestre"
                />
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Descrição
                </label>
                <textarea
                  value={achievementForm.description}
                  onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  rows={3}
                  placeholder="Descreva a conquista..."
                />
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Ícone (emoji)
                </label>
                <input
                  type="text"
                  value={achievementForm.icon || ''}
                  onChange={(e) => setAchievementForm({ ...achievementForm, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  placeholder="🏆"
                />
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Tipo de Condição <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  value={achievementForm.conditionType}
                  onChange={(e) => setAchievementForm({ ...achievementForm, conditionType: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                >
                  <option value="TASK_COUNT">Tarefas Completadas</option>
                  <option value="TASK_STREAK">Dias de Streak</option>
                  <option value="LEVEL_REACHED">Level Atingido</option>
                  <option value="POINTS_EARNED">Pontos Acumulados</option>
                  <option value="DAYS_ACTIVE">Dias Ativos</option>
                </select>
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Valor da Condição <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="number"
                  value={achievementForm.conditionValue}
                  onChange={(e) => setAchievementForm({ ...achievementForm, conditionValue: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  min={1}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Recompensa XP
                  </label>
                  <input
                    type="number"
                    value={achievementForm.xpReward || 0}
                    onChange={(e) => setAchievementForm({ ...achievementForm, xpReward: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                    min={0}
                  />
                </div>
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Recompensa Pontos
                  </label>
                  <input
                    type="number"
                    value={achievementForm.pointsReward || 0}
                    onChange={(e) => setAchievementForm({ ...achievementForm, pointsReward: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                    min={0}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="achievement-active"
                  checked={achievementForm.isActive ?? true}
                  onChange={(e) => setAchievementForm({ ...achievementForm, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label 
                  htmlFor="achievement-active"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Ativo
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAchievementModal(false);
                    setAchievementForm({
                      name: '',
                      description: '',
                      icon: '',
                      conditionType: 'TASK_COUNT',
                      conditionValue: 1,
                      xpReward: 0,
                      pointsReward: 0,
                      isActive: true,
                    });
                  }}
                  className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    createAchievement(achievementForm, {
                      onSuccess: () => {
                        setShowAchievementModal(false);
                        setAchievementForm({
                          name: '',
                          description: '',
                          icon: '',
                          conditionType: 'TASK_COUNT',
                          conditionValue: 1,
                          xpReward: 0,
                          pointsReward: 0,
                          isActive: true,
                        });
                      },
                      onError: (error: any) => {
                        alert(error.response?.data?.message || 'Erro ao criar conquista');
                      },
                    });
                  }}
                  disabled={!achievementForm.name || !achievementForm.conditionValue || isCreatingAchievement}
                  className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#03131B',
                    color: '#FFFFFF',
                  }}
                >
                  {isCreatingAchievement ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
          <div 
            className="bg-white p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                }}
              >
                Criar Evento Especial
              </h3>
              <button 
                onClick={() => setShowEventModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Nome <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  placeholder="Ex: Evento de Pontos 2x, Boost de XP"
                />
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Descrição
                </label>
                <textarea
                  value={eventForm.description || ''}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  rows={3}
                  placeholder="Descreva o evento..."
                />
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Tipo de Evento <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  value={eventForm.eventType}
                  onChange={(e) => {
                    const newType = e.target.value as any;
                    let defaultMultiplier = 2.0;
                    if (newType === 'XP_MULTIPLIER') defaultMultiplier = 1.5;
                    setEventForm({ ...eventForm, eventType: newType, multiplier: defaultMultiplier });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                >
                  <option value="POINTS_MULTIPLIER">Pontos 2x (Multiplicador de Pontos)</option>
                  <option value="XP_MULTIPLIER">Boost de XP (Multiplicador de Experiência)</option>
                  <option value="CUSTOM">Personalizado (Pontos e XP)</option>
                </select>
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Multiplicador <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={eventForm.multiplier}
                  onChange={(e) => setEventForm({ ...eventForm, multiplier: parseFloat(e.target.value) || 1.0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#2D3748',
                  }}
                  placeholder="2.0"
                />
                <p 
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#A0AEC0',
                    marginTop: '0.25rem',
                  }}
                >
                  Ex: 2.0 = dobra, 1.5 = 50% a mais, 3.0 = triplica
                </p>
              </div>
              <div>
                <label 
                  className="block mb-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Método de Duração <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="durationMethod"
                      value="dateRange"
                      checked={eventDurationMethod === 'dateRange'}
                      onChange={(e) => {
                        setEventDurationMethod('dateRange');
                        setEventForm({ ...eventForm, durationHours: null });
                      }}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748' }}>
                      Período (Data início/fim)
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="durationMethod"
                      value="hours"
                      checked={eventDurationMethod === 'hours'}
                      onChange={(e) => {
                        setEventDurationMethod('hours');
                        setEventForm({ ...eventForm, validFrom: null, validUntil: null });
                      }}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748' }}>
                      Duração em Horas
                    </span>
                  </label>
                </div>
              </div>
              {eventDurationMethod === 'dateRange' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label 
                      className="block mb-2"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#718096',
                      }}
                    >
                      Data Início <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={eventForm.validFrom ? new Date(eventForm.validFrom).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEventForm({ ...eventForm, validFrom: e.target.value ? new Date(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#2D3748',
                      }}
                    />
                  </div>
                  <div>
                    <label 
                      className="block mb-2"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#718096',
                      }}
                    >
                      Data Fim <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={eventForm.validUntil ? new Date(eventForm.validUntil).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEventForm({ ...eventForm, validUntil: e.target.value ? new Date(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#2D3748',
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label 
                    className="block mb-2"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#718096',
                    }}
                  >
                    Duração em Horas <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={eventForm.durationHours || ''}
                    onChange={(e) => setEventForm({ ...eventForm, durationHours: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                    }}
                    placeholder="24"
                  />
                  <p 
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#A0AEC0',
                      marginTop: '0.25rem',
                    }}
                  >
                    O evento começará imediatamente após a criação
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="event-active"
                  checked={eventForm.isActive ?? true}
                  onChange={(e) => setEventForm({ ...eventForm, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label 
                  htmlFor="event-active"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#718096',
                  }}
                >
                  Ativo
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setEventForm({
                      name: '',
                      description: '',
                      eventType: 'POINTS_MULTIPLIER',
                      multiplier: 2.0,
                      validFrom: null,
                      validUntil: null,
                      durationHours: null,
                      isActive: true,
                    });
                    setEventDurationMethod('dateRange');
                  }}
                  className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#F9FAFB',
                    color: '#2D3748',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const hasDateRange = eventForm.validFrom && eventForm.validUntil;
                    const hasDurationHours = eventForm.durationHours !== null && eventForm.durationHours !== undefined;
                    if (!hasDateRange && !hasDurationHours) {
                      alert('Defina um período (datas) ou duração em horas');
                      return;
                    }
                    createEvent(eventForm, {
                      onSuccess: () => {
                        setShowEventModal(false);
                        setEventForm({
                          name: '',
                          description: '',
                          eventType: 'POINTS_MULTIPLIER',
                          multiplier: 2.0,
                          validFrom: null,
                          validUntil: null,
                          durationHours: null,
                          isActive: true,
                        });
                        setEventDurationMethod('dateRange');
                      },
                      onError: (error: any) => {
                        alert(error.response?.data?.message || 'Erro ao criar evento');
                      },
                    });
                  }}
                  disabled={!eventForm.name || !eventForm.multiplier || isCreatingEvent}
                  className="px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    backgroundColor: '#03131B',
                    color: '#FFFFFF',
                  }}
                >
                  {isCreatingEvent ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

